import React, { useState } from 'react';
import { Box, TableCell, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import dayjs from 'dayjs';

const MORE_MIN_LENGTH = 36;

function ExpandableTextCell({ text, maxWidth = 130 }) {
  const [expanded, setExpanded] = useState(false);
  const display = text != null && text !== '' ? String(text) : '—';
  const showMore = display !== '—' && display.length > MORE_MIN_LENGTH;

  if (expanded) {
    return (
      <Box sx={{ maxWidth: maxWidth + 40 }}>
        <Typography
          sx={{
            fontSize: 'inherit',
            fontFamily: "'Jameel Noori Nastaleeq', serif",
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.4,
          }}
        >
          {display}
        </Typography>
        <Typography
          component='button'
          type='button'
          onClick={() => setExpanded(false)}
          sx={{
            fontSize: '0.7rem',
            color: 'primary.main',
            cursor: 'pointer',
            border: 0,
            background: 'none',
            p: 0,
            mt: 0.5,
            textDecoration: 'underline',
          }}
        >
          Less
        </Typography>
      </Box>
    );
  }

  const previewLen = Math.max(12, Math.floor(maxWidth / 7));
  const truncated =
    display.length > previewLen ? `${display.slice(0, previewLen).trimEnd()}...` : display;

  return (
    <Box
      component='span'
      title={display}
      sx={{
        maxWidth,
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        minWidth: 0,
        fontSize: 'inherit',
        fontFamily: "'Jameel Noori Nastaleeq', serif",
      }}
    >
      <Typography component='span' sx={{ fontSize: 'inherit', fontFamily: 'inherit' }}>
        {truncated}
      </Typography>
      {showMore && (
        <Typography
          component='button'
          type='button'
          onClick={() => setExpanded(true)}
          sx={{
            flexShrink: 0,
            fontSize: '0.7rem',
            color: 'primary.main',
            cursor: 'pointer',
            border: 0,
            background: 'none',
            p: 0,
            ml: 0.25,
            textDecoration: 'underline',
            whiteSpace: 'nowrap',
          }}
        >
          More
        </Typography>
      )}
    </Box>
  );
}

const parseMaxWidth = (sx) => {
  if (!sx?.maxWidth) return 130;
  const n = parseInt(sx.maxWidth, 10);
  return Number.isNaN(n) ? 130 : n;
};

export function TableCellComponent({ tableId, cellData, sx, rowData, compact = false }) {
  const navigate = useNavigate();

  const handleBookClick = (rowData) => {
    navigate(`/chapters?bookId=${rowData?.id}`);
  };

  const handleChapterClick = (rowData) => {
    navigate(`/contents?chapterId=${rowData?.id}`);
  };

  const getDirection = (text) => {
    const firstChar = text ? text?.trim().charAt(0) : "";
    return /[\u0600-\u06FF]/.test(firstChar) ? 'rtl' : 'ltr'; // Urdu/Arabic Unicode range
  };


  const renderCellContent = () => {
    switch (tableId) {
      case 'url':
        if (cellData && /\.(jpg|jpeg|png|gif|webp|svg)/i.test(cellData)) {
          const size = compact ? 36 : 50;
          return (
            <Box
              component='a'
              href={cellData}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ display: 'inline-block', lineHeight: 0 }}
            >
              <img
                src={cellData}
                alt=''
                style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4 }}
              />
            </Box>
          );
        }
        return (
          <Typography
            component='a'
            href={cellData}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              display: 'inline-block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'primary.main',
              fontFamily: "'Jameel Noori Nastaleeq', serif",
              maxWidth: compact ? 90 : 200,
              fontSize: 'inherit',
            }}
            title={cellData}
          >
            {cellData}
          </Typography>
        );

      case 'bookName':
        return (
          <Typography
            sx={{
              direction: getDirection(cellData),
              // textAlign: getDirection(cellData) === 'rtl' ? 'right' : 'left',
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
              fontFamily: "'Jameel Noori Nastaleeq', serif",
            }}
            onClick={() => handleBookClick(rowData)}
          >
            {cellData}
          </Typography>
        )
      case 'chapterName':
        return (
          <Typography
            sx={{
              direction: getDirection(rowData?.Chapter?.Book?.bookName),
              // textAlign: getDirection(rowData?.Chapter?.Book?.bookName) === 'rtl' ? 'right' : 'left',
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
              fontFamily: "'Jameel Noori Nastaleeq', serif",
            }}
            onClick={() => handleChapterClick(rowData)}
          >
            {cellData}
          </Typography>
        )

      case 'coverImage':
        return (
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px dashed #ccc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              cursor: 'pointer',
              backgroundColor: '#f5f5f5',
            }}
          >
            {cellData ? (
              <img
                src={cellData}
                alt="Cover"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <PhotoCameraIcon sx={{ fontSize: 32, color: '#888' }} />
            )}
          </Box>
        )
        case 'contentBookId':
        return (
          <Typography
           sx={{
            direction: getDirection(rowData?.Chapter?.Book?.bookName),
            fontFamily: "'Jameel Noori Nastaleeq', serif",
            // textAlign: getDirection(rowData?.Chapter?.Book?.bookName) === 'rtl' ? 'right' : 'left',
           }}
          >
            {rowData?.Chapter?.Book?.bookName}
          </Typography>
        )
        case 'bookNoContent':
        return (
          <Typography
          sx={{
            fontFamily: "'Jameel Noori Nastaleeq', serif",
          }}
          >
            {rowData?.Chapter?.Book?.bookNo}
          </Typography>
        )
        case 'chapterNoContent':
        return (
          <Typography
          sx={{
            fontFamily: "'Jameel Noori Nastaleeq', serif",
          }}
          >
            {rowData?.Chapter?.chapterNo}
          </Typography>
        )
      case 'title':
      case 'description':
        return <ExpandableTextCell text={cellData} maxWidth={parseMaxWidth(sx)} />;

      case 'scheduledAt':
        return (
          <Typography
            sx={{
              fontFamily: "'Jameel Noori Nastaleeq', serif",
              fontSize: 'inherit',
            }}
          >
            {cellData
              ? dayjs(cellData).format(compact ? 'DD/MM/YY HH:mm' : 'DD/MM/YYYY HH:mm')
              : '—'}
          </Typography>
        )

      case 'isExternal':
        return (
          <Typography sx={{ fontFamily: "'Jameel Noori Nastaleeq', serif" }}>
            {cellData === false ? 'Yes' : '—'}
          </Typography>
        )
      // You can add more cases here in the future like:
      // case 'chapterId':
      //   return ...

      default:
        return cellData;
    }
  };

  return (
    <TableCell
      align='left'
      sx={{
        fontFamily: "'Jameel Noori Nastaleeq', serif",
        fontSize: compact ? '0.8125rem' : '16px',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {renderCellContent()}
    </TableCell>
  );
}

export default TableCellComponent;
